// v0.5.0i+4 — Extracted from lib/api.js during the 500-LOC cap
// refactor. Houses the three blob-download helpers (XLSX export +
// IOSS XML + OSS XML). All share the same `responseType: "blob"`
// dance: parse Content-Disposition for the filename, surface
// server-side JSON errors that arrive as Blobs.
//
// The public surface is re-exported from lib/api.js for
// backwards-compat with every component that does
// `import { downloadCalcXlsx } from "../lib/api"`.
import axios from "axios";
import { getToken, clearToken } from "../authApi";
import { API } from "../backend";
import { installStagingInterceptors } from "../stagingGate";
import { installWriteGenerations } from "../sharedRead";

// Mirror the auth + 401 interceptor on a dedicated axios instance —
// downloads run on the same auth contract as every other /api/* call.
// v0.6.6 — that included the staging-gate header/401 handling in intent
// (see comment above) but never actually called installStagingInterceptors
// here, so this instance never sent X-Staging-Access at all (only the
// staging_access cookie fallback covered it) and had no staging_locked
// detection of its own.
const client = axios.create({ baseURL: API, timeout: 30000 });
installStagingInterceptors(client);
installWriteGenerations(client);   // A1-95: see lib/sharedRead.js
client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
client.interceptors.response.use(
  (r) => r,
  (e) => {
    // v0.6.6 — see the identical fix + comment in lib/api.js: a
    // staging_locked 401 must not also clear the real admin session.
    if (e?.response?.status === 401 && !e.response.data?.staging_locked) {
      clearToken();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(e);
  },
);

export function parseSafetyWarnings(headers) {
  const n = Number(headers?.["x-client-safety-warnings"] || 0);
  if (!n) return null;
  let cells = [];
  try { cells = JSON.parse(headers?.["x-client-safety-detail"] || "[]"); } catch { cells = []; }
  return { count: n, cells: Array.isArray(cells) ? cells : [] };
}

export const xlsxExportUrl = (calcId) => `${API}/runs/calc/${calcId}/export.xlsx`;

// Authenticated XLSX download. Returns { ok, error? } — never throws,
// so a refused export (e.g. 409 SIGNOFF_REQUIRED) doesn't trip the CRA
// dev-overlay's unhandled-rejection trap.
export const downloadCalcXlsx = async (calcId) => {
  try {
    const res = await client.get(`/runs/calc/${calcId}/export.xlsx`, {
      responseType: "blob",
    });
    const disp = res.headers?.["content-disposition"] || "";
    const match = disp.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const filename = match
      ? decodeURIComponent(match[1])
      : `vat-${calcId}.xlsx`;
    const blobUrl = window.URL.createObjectURL(
      new Blob([res.data], {
        type:
          res.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
    );
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    // A1-22 (operator 2026-10-12: warn, never block) — the server scans the
    // rendered workbook for text not meant for a client and reports it here.
    return { ok: true, safetyWarnings: parseSafetyWarnings(res.headers) };
  } catch (e) {
    let detail = e?.message || "Export failed";
    let breaches = null;
    const status = e?.response?.status;
    const blob = e?.response?.data;
    if (blob && typeof blob.text === "function") {
      try {
        const txt = await blob.text();
        const parsed = JSON.parse(txt);
        if (parsed?.detail?.message) detail = parsed.detail.message;
        else if (typeof parsed?.detail === "string") detail = parsed.detail;
        else if (parsed?.message) detail = parsed.message;
        else detail = txt;
        // v0.4.77 Q2=a — surface the structured breaches dict so the
        // Step 6 error banner can render "→ Open <country>" deep-links
        // alongside the prose message.
        if (parsed?.detail?.breaches) breaches = parsed.detail.breaches;
      } catch {
        /* leave detail as-is */
      }
    }
    if (status === 409 && typeof detail === "string"
        && detail.includes("SIGNOFF_REQUIRED")) {
      detail = "Sign off this calculation first, then export.";
    }
    return { ok: false, status, error: detail, breaches };
  }
};

// v0.6 — Filing-package "download both" zip. Same blob dance; surfaces the
// per-calc export gate (e.g. 409 SIGNOFF_REQUIRED) verbatim from the server.
export const downloadPackageZip = async (packageId) => {
  try {
    const res = await client.get(
      `/filing-packages/${packageId}/export-both.zip`,
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(
      new Blob([res.data], { type: "application/zip" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `filing-package-${packageId}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    return { ok: true };
  } catch (e) {
    let detail = e?.message || "Download failed";
    const blob = e?.response?.data;
    if (blob && typeof blob.text === "function") {
      try {
        const parsed = JSON.parse(await blob.text());
        detail = parsed?.detail?.message
          || (typeof parsed?.detail === "string" ? parsed.detail : detail);
      } catch { /* leave detail as-is */ }
    }
    return { ok: false, status: e?.response?.status, error: detail };
  }
};


// v0.4.22 Tier-3 — IOSS XML download. Same blob-download pattern as
// the XLSX export. Failure detail (CALC_NOT_FROZEN /
// IOSS_MSI_NOT_CONFIGURED / NO_IOSS_ROWS) is surfaced verbatim from
// the server so the operator sees the precise next-step instruction.
const _downloadXmlBlob = async (path, defaultName) => {
  try {
    const res = await client.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
    const a = document.createElement("a");
    a.href = url;
    const cd = res.headers["content-disposition"] || "";
    const m = /filename="([^"]+)"/.exec(cd);
    a.download = m?.[1] || defaultName;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    return { ok: true };
  } catch (e) {
    // Error body arrives as a Blob (because responseType=blob).
    // Read it as text and try to parse the JSON detail.
    let msg = e?.message || "XML export failed";
    const blob = e?.response?.data;
    if (blob && typeof blob.text === "function") {
      try {
        const txt = await blob.text();
        const parsed = JSON.parse(txt);
        msg = parsed?.detail?.message
          || (typeof parsed?.detail === "string" ? parsed.detail : msg);
      } catch { /* keep default */ }
    } else if (blob && typeof blob === "object") {
      msg = blob.detail?.message || blob.detail || msg;
    }
    return { ok: false, error: msg, status: e?.response?.status };
  }
};

export const downloadIossXml = (calcId) =>
  _downloadXmlBlob(`/iossfilings/${calcId}/xml`, `ioss-${calcId}.xml`);

export const downloadOssXml = (calcId) =>
  _downloadXmlBlob(`/ossfilings/${calcId}/xml`, `oss-${calcId}.xml`);

// v0.5.0i+21 — Non-Union OSS XML export. Backend route added in
// v0.5.0i+19c (`/api/non-union-ossfilings/{calc_id}/xml`). Populated
// from rows classified into `EU_NON_UNION_OSS` by the Art 58 rule
// (UK / non-EU sellers of digital services to EU consumers).
export const downloadNonUnionOssXml = (calcId) =>
  _downloadXmlBlob(
    `/non-union-ossfilings/${calcId}/xml`,
    `non-union-oss-${calcId}.xml`,
  );

// v0.6.12 (A2-222) — Skatteverket upload file for the Swedish return
// (eSKDUpload 6.0). The server names the file from the organisation
// number and period; the fallback only shows if that header is missing.
export const downloadSeMomsXml = (calcId) =>
  _downloadXmlBlob(
    `/calc/${calcId}/country-return/SE/xml-payload`,
    `momsdeklaration-${calcId}.xml`,
  );

// Roadmap 1b.9 (A2-305) — Italy's LIPE file (Fornitura IVP18), to upload
// through Entratel / Fisconline. Monthly filers get one module per month.
export const downloadItLipeXml = (calcId) =>
  _downloadXmlBlob(
    `/calc/${calcId}/country-return/IT/xml-payload`,
    `lipe-${calcId}.xml`,
  );

// v0.6.9 — Settings > Exchange Rates preset. Same blob-download dance
// as every other export above.
const _downloadXlsxBlob = async (path, defaultName) => {
  try {
    const res = await client.get(path, { responseType: "blob" });
    const disp = res.headers?.["content-disposition"] || "";
    const match = disp.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const filename = match ? decodeURIComponent(match[1]) : defaultName;
    const blobUrl = window.URL.createObjectURL(
      new Blob([res.data], {
        type:
          res.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
    );
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    return { ok: true };
  } catch (e) {
    let detail = e?.message || "Download failed";
    const blob = e?.response?.data;
    if (blob && typeof blob.text === "function") {
      try {
        const parsed = JSON.parse(await blob.text());
        detail = parsed?.detail?.message
          || (typeof parsed?.detail === "string" ? parsed.detail : detail);
      } catch { /* keep default */ }
    }
    return { ok: false, status: e?.response?.status, error: detail };
  }
};

export const downloadFxRatesTemplate = () =>
  _downloadXlsxBlob(
    "/settings/fx-rates/template.xlsx", "vat-support-fx-rates-template.xlsx",
  );

export const downloadCurrentFxRates = () =>
  _downloadXlsxBlob(
    "/settings/fx-rates/export.xlsx", "vat-support-fx-rates-current.xlsx",
  );

// v0.6.9 Phase 2 — Settings > VAT Rates manual override.
export const downloadVatRatesTemplate = () =>
  _downloadXlsxBlob(
    "/settings/vat-rates/template.xlsx", "vat-support-vat-rates-template.xlsx",
  );

export const downloadResolvedVatRates = () =>
  _downloadXlsxBlob(
    "/settings/vat-rates/export.xlsx", "vat-support-vat-rates-resolved.xlsx",
  );

// Roadmap 2.2 — every rule the app applies (deduction rules, Art 194/59a
// elections, form editions), one row per version, for review.
export const downloadRulesWorkbook = () =>
  _downloadXlsxBlob("/settings/rules/export.xlsx", "vat-support-rules.xlsx");

// v0.6.12 — the client template, now over the authenticated client.
//
// It was two plain `<a href>` anchors, so the browser made a top-level GET
// with no Authorization header — which is WHY the route had to be mounted
// without auth. That left an unauthenticated endpoint that takes a
// caller-supplied client_id and stamps the resolved CLIENT NAME onto the
// Guide sheet ("Prepared for: <company>"), i.e. anyone who could reach the
// API and hold a client id could read that client's name back out.
//
// Exactly the fix ClientHistory.jsx already applied for the calc export
// ("was a plain <a href=...export.xlsx>"): fetch it as a blob on the
// authenticated axios instance and click a synthetic anchor. The server's
// own Content-Disposition still names the file, and _downloadXlsxBlob reads
// the response's content-type, so the CSV variant works unchanged.
export const downloadClientTemplate = (format = "xlsx", clientId = null) => {
  const qs = new URLSearchParams({ format });
  if (clientId) qs.set("client_id", clientId);
  return _downloadXlsxBlob(
    `/runs/client-template/download?${qs.toString()}`,
    `vat-support-client-template.${format === "csv" ? "csv" : "xlsx"}`,
  );
};

// v0.5.9m — re-download an upload's data as an XLSX, reconstructed
// exactly as ingested from the stored rows. Same blob-download +
// generic-error contract as downloadCalcXlsx; never throws.
export const downloadIngestRun = async (runId, fallbackName = null) => {
  try {
    const res = await client.get(`/runs/ingest/${runId}/download.xlsx`, {
      responseType: "blob",
    });
    const disp = res.headers?.["content-disposition"] || "";
    const match = disp.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const filename = match
      ? decodeURIComponent(match[1])
      : (fallbackName || `upload-${runId}.xlsx`);
    const blobUrl = window.URL.createObjectURL(
      new Blob([res.data], {
        type:
          res.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
    );
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    return { ok: true };
  } catch (e) {
    let detail = e?.message || "Download failed";
    const blob = e?.response?.data;
    if (blob && typeof blob.text === "function") {
      try {
        const parsed = JSON.parse(await blob.text());
        detail = parsed?.detail?.message
          || (typeof parsed?.detail === "string" ? parsed.detail : detail);
      } catch { /* keep default */ }
    }
    return { ok: false, status: e?.response?.status, error: detail };
  }
};
